/* eslint-disable no-console */
import Script from 'next/script';
import mixpanel from 'mixpanel-browser';
import { memo, useEffect, useState } from 'react';
import { useMetaRouterOptOutCookie } from '@bridebook/web/app-shared/lib/app/hooks/use-meta-router-opt-out-cookie';
import { env } from '@bridebook/web/lib/env';

type MetaRouterScriptProps = { isCordova: boolean };

export const MetaRouterScript = memo(function MetaRouterScript({
  isCordova,
}: MetaRouterScriptProps) {
  useMetaRouterOptOutCookie();

  useEffect(() => {
    if (!env.MIXPANEL_TOKEN) {
      return;
    }

    mixpanel.init(env.MIXPANEL_TOKEN, {
      debug: false,
      persistence: 'localStorage',
      record_block_selector: 'video',
      record_heatmap_data: true,
      record_mask_text_selector: '[type="password"]',
      /**
       * Minimum session duration before session recording starts.
       * Set to 8 seconds to avoid recording very short sessions, which are unlikely to provide useful analytics.
       */
      record_min_ms: 8_000,
      /**
       * 4% session recording rate based an estimated 500K total sessions per month.
       */
      record_sessions_percent: env.LIVE ? 4 : 100,
      track_pageview: false,
    });

    /**
     * @description https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript/javascript-replay#segment-analyticsjs
     */
    const registerMiddleware = () => {
      /**
       * TODO: Properly type window.analytics and window.mixpanel in global type definitions.
       */
      const window$ = window as {
        analytics?: {
          addSourceMiddleware: (middleware: any) => void;
        };
        mixpanel?: unknown;
      };

      if (window$.analytics?.addSourceMiddleware) {
        window$.analytics.addSourceMiddleware(({ payload, next }: any) => {
          try {
            if (window$.mixpanel) {
              if (['page', 'track'].includes(payload.obj.type)) {
                const segmentDeviceId = payload.obj.anonymousId;

                // Simplified ID Merge
                mixpanel.register({
                  $device_id: segmentDeviceId,
                  distinct_id: '$device:' + segmentDeviceId,
                });

                payload.obj.properties = {
                  ...payload.obj.properties,
                  ...mixpanel.get_session_recording_properties(),
                };
              } else if (payload.obj.type === 'identify') {
                mixpanel.identify(payload.obj.userId);
              }
            }
          } catch (error) {
            console.error('MixPanel middleware error:', error);
          }

          next(payload);
        });

        return true;
      }

      return false;
    };

    /**
     * Retry registering middleware until analytics is ready.
     */
    if (!registerMiddleware()) {
      const timeout = setInterval(() => {
        if (registerMiddleware()) {
          clearInterval(timeout);
        }
      }, 500);

      return () => clearInterval(timeout);
    }
  }, []);

  const analyticsScript = `!function(){var e=window.analytics=window.analytics||[];if(!e.initialize)if(e.invoked)window.console&&console.error&&console.error("MetaRouter snippet included twice.");else{e.invoked=!0,e.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"],e.factory=function(t){return function(){var o=Array.prototype.slice.call(arguments);return o.unshift(t),e.push(o),e}};for(var t=0;t<e.methods.length;t++){var o=e.methods[t];e[o]=e.factory(o)}e.load=function(t,o){var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="/mr${
    env.LIVE ? '' : '-dev'
  }.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(a,n),e._loadOptions=o},e.SNIPPET_VERSION="4.13.1";`;
  const sessionId = '';
  const segmentId = env.SEGMENTID;
  return (
    <Script
      id="metarouter_script"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `${analyticsScript}analytics.load("${segmentId}");var a=window.location.href,n=window.location.pathname;e.page(n,{title:n,path:n,url:a,mobileApp:${isCordova},sessionId:"${sessionId}"})}}();`,
      }}
    />
  );
});

export const AppBannerScript = memo(function AppBannerScript({
  showBanner,
}: {
  showBanner: boolean;
}) {
  const AFReadyInitValue = typeof window !== 'undefined' && !!window.AF;
  const [isAFReady, setIsAFReady] = useState(AFReadyInitValue);

  useEffect(() => {
    if (isAFReady) {
      const AF = window.AF;
      AF('banners', 'hideBanner');
    }
  }, [isAFReady]);

  if (!showBanner) return null;

  return (
    <Script
      id="app_banner_script"
      onLoad={() => setIsAFReady(true)}
      strategy="lazyOnload"
      src="/scripts/app-banner.js"
    />
  );
});
